'use es6';

import I18n from 'I18n';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import { List } from 'immutable';
import { Fragment } from 'react';
import { renderToString } from 'react-dom/server';
import { View } from 'foundations-components/components/View';
import { ReportingLogger } from 'reporting-data/monitoring/reportingLogger';
import { getProperty, getReference, getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { CustomHighchartsTooltip, TooltipHeader, TooltipBody, TooltipBodyLabel, TooltipBodyListContainer, TooltipBodyValue, TooltipSeriesSymbol, TooltipBodySelected } from '../../../../highcharts/options/customTooltipStyles';
import { HR } from 'foundations-components/transitional/components/HR';
import { getPropertyFormatter } from '../../../../formatters/propertyFormatter';
import { AxisTypes, ExcludePointsFromTooltipScopes, MarkTypes } from '../../xyRecords';
import { getAggregationType } from '../../../../utils/layerUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const renderTooltip = (data, totalLabel, showZeroesInTooltip) => {
  const selectedItem = data.find(({
    selected
  }) => selected) || data.first();
  const selectedCategory = selectedItem && selectedItem.category;
  const header = selectedCategory === 'undefined' ? '' : selectedCategory;
  const tooltipData = showZeroesInTooltip ? data : data.filter(({
    rawValue
  }) => rawValue !== 0);
  const body = tooltipData.map(({
    name,
    value,
    selected,
    color,
    percentage
  }, key) => {
    return /*#__PURE__*/_jsxs(TooltipBodyListContainer, {
      children: [/*#__PURE__*/_jsxs(TooltipBodyLabel, {
        children: [/*#__PURE__*/_jsx(TooltipSeriesSymbol, {
          color: color
        }), /*#__PURE__*/_jsx(TooltipBodySelected, {
          selected: selected,
          children: I18n.text('reporting-visualizations.tooltip.group', {
            group: I18n.SafeString(name)
          })
        })]
      }), /*#__PURE__*/_jsxs(TooltipBodyValue, {
        children: [value, percentage != null ? /*#__PURE__*/_jsxs(View, {
          marginInlineStart: "100",
          children: ["(", /*#__PURE__*/_jsx(FormattedPercentage, {
            value: percentage * 100
          }), ")"]
        }) : null]
      })]
    }, key);
  }).toList();
  const tooltip = /*#__PURE__*/_jsxs(CustomHighchartsTooltip, {
    children: [/*#__PURE__*/_jsx(TooltipHeader, {
      children: header
    }), /*#__PURE__*/_jsxs(TooltipBody, {
      children: [body, (showZeroesInTooltip ? Number.isFinite(totalLabel) : totalLabel) ? /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(HR, {
          distance: "extra-small"
        }, "hr"), /*#__PURE__*/_jsxs(TooltipBodyListContainer, {
          children: [/*#__PURE__*/_jsxs(TooltipBodyLabel, {
            children: [/*#__PURE__*/_jsx(TooltipSeriesSymbol, {}), I18n.text('reporting-ui-components.tooltip.totals')]
          }), /*#__PURE__*/_jsx(TooltipBodyValue, {
            children: totalLabel
          })]
        }, "totalsRow")]
      }) : null]
    })]
  });
  return renderToString(tooltip);
};
const getSeriesWithoutTooltipExclusions = (chart, layerMap) => {
  return chart.get('series').reduce((allSeriesWithoutExcludedPoints, currentSeries) => {
    const currentSeriesLayer = layerMap.get(currentSeries.get('layerId'));
    const tooltipExclusion = currentSeriesLayer.get('excludePointsFromTooltip');
    const exclusionScope = tooltipExclusion === null || tooltipExclusion === void 0 ? void 0 : tooltipExclusion.scope;
    if (exclusionScope === ExcludePointsFromTooltipScopes.all) {
      return allSeriesWithoutExcludedPoints;
    }
    if (exclusionScope === ExcludePointsFromTooltipScopes.range) {
      const currentSeriesPoints = currentSeries.get('points') || List([]);
      const exclusionFrom = (tooltipExclusion === null || tooltipExclusion === void 0 ? void 0 : tooltipExclusion.from) || 0; // default to start of data if not provided
      const exclusionTo = (tooltipExclusion === null || tooltipExclusion === void 0 ? void 0 : tooltipExclusion.to) || currentSeriesPoints.size; // default to end of data if not provided

      return allSeriesWithoutExcludedPoints.push(currentSeries.set('points', currentSeriesPoints.map((point, index) => {
        if (exclusionFrom <= index && index <= exclusionTo) {
          return point.set('hideInTooltip', true);
        }
        return point;
      })));
    }
    return allSeriesWithoutExcludedPoints.push(currentSeries);
  }, List([]));
};
export const getTooltipFormatter = ({
  chart,
  layers
}) => {
  const layerMap = layers.toMap().mapKeys((_, layer) => layer.get('id'));
  const seriesMap = getSeriesWithoutTooltipExclusions(chart, layerMap).toMap().mapKeys((_, series) => series.get('id'));
  const primaryLayer = layerMap.first();
  let categoryChannel = primaryLayer.getIn(['x', 'type']) === AxisTypes.category ? 'x' : 'y';
  let valueChannel = categoryChannel === 'x' ? 'y' : 'x';
  if (!primaryLayer || primaryLayer.getIn(['x', 'type']) !== AxisTypes.category && primaryLayer.getIn(['y', 'type']) !== AxisTypes.category) {
    categoryChannel = undefined;
    valueChannel = primaryLayer.getIn(['x', 'type']) === AxisTypes.scaffold ? 'y' : 'x';
  }
  const stacked = layerMap.some(layer => layer.getIn(['mark', 'stacking']));
  const shouldShowTotalsBasedOnData = stacked && layerMap.every(layer => {
    const type = getAggregationType({
      channel: valueChannel,
      layer
    });
    return ['SUM', 'COUNT', 'DISTINCT_APPROX'].includes(type);
  });
  const showTotals = chart.getIn(['options', 'showTotals']) === undefined ? shouldShowTotalsBasedOnData : chart.getIn(['options', 'showTotals']);
  const showZeroesInTooltip = chart.getIn(['options', 'showZeroesInTooltip']);
  return function formatter() {
    const categoryIndex = this.point.x;
    const highchartsSeriesLookup = List(this.series.chart.series).toMap().mapKeys((_, series) => series.userOptions.id);
    const data = seriesMap.toList().flatMap(serie => serie.get('points').map((p, i) => p.set('index', i)).filter(p => {
      const hideInTooltip = p && p.get('hideInTooltip');
      const value = p && p.get('value');
      return !hideInTooltip && value && value[0] === categoryIndex;
    })).map(point => {
      var _getProperty;
      const layer = layerMap.get(point.get('layerId'));
      const dataset = layer.get('dataset');
      const markType = layer.getIn(['mark', 'type']);
      const seriesId = point.get('seriesId');
      const series = seriesMap.get(seriesId);
      const seriesIndex = series.get('index');
      const name = series.get('name');
      const selected = seriesId === this.series.userOptions.id && point.get('index') === this.point.index;
      const category = categoryChannel ? getReferenceLabel(dataset, layer.getIn([categoryChannel, 'field']), point.getIn(['row', layer.getIn([categoryChannel, 'field'])])) : undefined;
      const valueField = layer.getIn([valueChannel, 'field']);
      const maybeMultiFormattingObject = layer.getIn([valueChannel, 'formattingObjectMap', valueField]);
      const maybeSingleFormattingObject = layer.getIn([valueChannel, 'formattingObject']);
      const maybeFormattingObject = maybeSingleFormattingObject || maybeMultiFormattingObject;
      const valueProperty = (_getProperty = getProperty(dataset, valueField)) === null || _getProperty === void 0 ? void 0 : _getProperty.set('formattingObject', maybeFormattingObject);
      const rawValueFromData = point.getIn(['row', valueField]);
      const axisTotal = point.getIn(['row', layer.axisTotal]);
      /**
       * If the mark type is arearange, then the data is an array of length 2
       * and should be displayed as a range, for example: 100-200.
       */
      const rawValue = markType === MarkTypes.arearange ? rawValueFromData.join(' - ') : rawValueFromData;
      if (!valueProperty) {
        const rl = new ReportingLogger();
        rl.addAttributes({
          valueField,
          propertyNames: dataset.properties.keySeq().toList(),
          datasetKeys: dataset.data.get(0).keySeq().toList()
        });
        rl.sendPageAction('RAAS_LEGACY_TOOLTIP_VALUE_FIELD');
      }
      const currencyCodeColumnName = valueProperty && valueProperty.getIn(['format', 'currencyCodeColumnName']);
      const columnCurrency = currencyCodeColumnName && point.getIn(['row', currencyCodeColumnName]);
      const referenceKey = columnCurrency ? `${columnCurrency}|${rawValue}` : rawValue;
      const reference = getReference(dataset, valueField, referenceKey) || getReference(dataset, valueField, rawValue);
      const referenceLabel = reference && reference.get('label');
      const valueLabel = referenceLabel || (valueProperty ? getPropertyFormatter(valueProperty)(rawValue) : rawValue);
      const value = valueLabel === 'undefined' ? '-' : valueLabel;
      const highchartsSeries = highchartsSeriesLookup.get(seriesId);
      const color = highchartsSeries.color;
      const tooltipPointData = {
        seriesIndex,
        name,
        selected,
        category,
        value,
        rawValue,
        color,
        axisTotal
      };
      if (shouldShowTotalsBasedOnData) {
        var _seriesMap$toList$fla;
        const selectedLayer = layerMap.get((_seriesMap$toList$fla = seriesMap.toList().flatMap(serie => {
          return serie.get('points').map((p, i) => {
            return p.set('index', i);
          }).filter(p => {
            const selectedLayerValue = p && p.get('value');
            return selectedLayerValue && selectedLayerValue[0] === categoryIndex;
          });
        }).filter(val => val.get('seriesId') === this.series.userOptions.id && val.get('index') === this.point.index).first()) === null || _seriesMap$toList$fla === void 0 ? void 0 : _seriesMap$toList$fla.get('layerId'));
        return Object.assign({}, tooltipPointData, {
          inSelectedData: dataset.get('key') === (selectedLayer === null || selectedLayer === void 0 ? void 0 : selectedLayer.getIn(['dataset', 'key'])),
          excludeFromLegacyTooltipTotals: layer.get('excludeFromLegacyTooltipTotals')
        });
      }
      return tooltipPointData;
    }).sortBy(({
      seriesIndex
    }) => seriesIndex).filter(({
      seriesIndex
    }, pointIndex, pointDataList) => pointDataList.findIndex(({
      seriesIndex: comparisonSeriesIndex
    }) => comparisonSeriesIndex === seriesIndex) === pointIndex);
    const getTotalBasedOnData = () => {
      return (shouldShowTotalsBasedOnData ? data.filter(point => point.inSelectedData && !point.excludeFromLegacyTooltipTotals) : data).reduce((accum, point) => {
        if (point.rawValue == null) {
          return accum;
        }
        return accum + point.rawValue;
      }, 0);
    };
    const totalBasedOnData = getTotalBasedOnData();
    const getSuppliedAxisTotal = () => {
      const selectedData = shouldShowTotalsBasedOnData ? data.filter(point => point.inSelectedData && !point.excludeFromLegacyTooltipTotals) : data;
      const firstData = selectedData.find(point => point.axisTotal);
      return firstData && firstData.axisTotal;
    };
    const suppliedAxisTotal = getSuppliedAxisTotal();
    const totalRawValue = showTotals && (suppliedAxisTotal == null ? totalBasedOnData : suppliedAxisTotal);
    const dataWithPercentages = showTotals ? data.map(point => {
      const shouldShowPercentage = point.inSelectedData && !point.excludeFromLegacyTooltipTotals && (showZeroesInTooltip ? Number.isFinite(point.rawValue) : point.rawValue && totalRawValue && totalRawValue > 0);
      if (!shouldShowPercentage) {
        return point;
      }
      return Object.assign({}, point, {
        percentage: totalRawValue === 0 ? 0 : point.rawValue / totalRawValue
      });
    }) : data;
    const valueField = primaryLayer.getIn([valueChannel, 'field']);
    const dataSet = primaryLayer.get('dataset');
    const valueProperty = getProperty(dataSet, valueField);
    const totalLabel = showTotals ? getPropertyFormatter(valueProperty)(totalRawValue) : undefined;
    return renderTooltip(dataWithPercentages, totalLabel, showZeroesInTooltip);
  };
};
export const __TESTABLE__ = {
  getSeriesWithoutTooltipExclusions
};